import { taipeiLevelTime } from './MemberLevelGrants';
/**
 * @component MemberCrmProfile
 * @description 會員詳情：CRM 匯入的個人資料、遮罩身分證字號（完整號碼需
 * member:view_national_id，每次檢視都寫入稽核），以及唯讀的「CRM 來源」面板。
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { MemberDto, MemberSourceRecordDto } from '@pokerroom/types';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-base-300 rounded-lg p-3 min-w-0 [overflow-wrap:anywhere]">
      <p className="text-xs text-base-content/60 mb-1">{label}</p>
      <div className="text-sm font-medium text-base-content">{children}</div>
    </div>
  );
}

export function MemberCrmProfile({
  member,
  section = 'all',
}: {
  member: MemberDto;
  section?: 'all' | 'profile' | 'sources';
}) {
  const { t } = useTranslation('member');
  const [revealed, setRevealed] = useState<string | null>(null);

  const { data: sources } = useQuery<MemberSourceRecordDto[]>({
    queryKey: ['member-source-records', member.id],
    queryFn: () => api.get<MemberSourceRecordDto[]>(`/members/${member.id}/source-records`),
  });

  const reveal = useMutation({
    mutationFn: () => api.post<{ nationalId: string }>(`/members/${member.id}/national-id/reveal`),
    onSuccess: (data) => setRevealed(data.nationalId),
  });
  const revealStatus = (reveal.error as { response?: { status?: number } } | null)?.response
    ?.status;

  const profile: [string, string | null | undefined][] = [
    ['realName', member.realName],
    ['birthDate', member.birthDate?.slice(0, 10)],
    ['gender', member.gender],
    ['address', member.address],
    [
      'levelExpiresAt',
      member.levelExpiresAt
        ? member.associationMode
          ? taipeiLevelTime(member.levelExpiresAt)
          : member.levelExpiresAt.slice(0, 10)
        : null,
    ],
  ];
  const hasNationalId = !!member.nationalIdMasked || !!member.nationalIdUnreadable;

  return (
    <>
      {section !== 'sources' && (profile.some(([, value]) => value) || hasNationalId) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {profile
            .filter(([, value]) => value)
            .map(([key, value]) => (
              <Field key={key} label={t(key)}>
                {value}
              </Field>
            ))}
          {hasNationalId && (
            <Field label={t('nationalId')}>
              {member.nationalIdUnreadable ? (
                <span className="text-error">{t('nationalIdUnreadable')}</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono" data-testid="member-national-id">
                    {revealed ?? member.nationalIdMasked}
                  </span>
                  {revealed ? (
                    <button
                      onClick={() => setRevealed(null)}
                      className="px-2 py-0.5 rounded border border-base-content/20 text-xs"
                    >
                      {t('nationalIdHide')}
                    </button>
                  ) : (
                    <button
                      onClick={() => reveal.mutate()}
                      disabled={reveal.isPending}
                      title={t('nationalIdRevealHint')}
                      className="px-2 py-0.5 rounded border border-base-content/20 text-xs disabled:opacity-50"
                    >
                      {t('nationalIdReveal')}
                    </button>
                  )}
                  {reveal.isError && (
                    <span className="text-xs text-error w-full">
                      {revealStatus === 403
                        ? t('nationalIdRevealDenied')
                        : t('nationalIdRevealFailed')}
                    </span>
                  )}
                </div>
              )}
            </Field>
          )}
        </div>
      )}

      {section !== 'profile' && sources && sources.length > 0 && (
        <details
          className="mb-4 rounded border border-base-300 p-3"
          data-testid="member-crm-source"
        >
          <summary className="cursor-pointer font-semibold">{t('linePanel.crmSources')}</summary>
          <h2 className="text-lg font-semibold text-base-content mb-3">{t('crmSourceTitle')}</h2>
          {sources.map((source) => (
            <div
              key={`${source.sourceSystem}:${source.sourceKey}`}
              className="bg-base-200 border border-base-300 rounded-lg p-3 text-sm"
            >
              <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2">
                <span>
                  <span className="text-base-content/60 mr-2">{t('crmSourceKey')}</span>
                  <span className="font-mono">{source.sourceKey}</span>
                </span>
                <span>
                  <span className="text-base-content/60 mr-2">{t('crmSourceMatch')}</span>
                  {t(`crmMatch_${source.matchResult}`, { defaultValue: source.matchResult })}
                </span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                {Object.entries(source.reference).map(([key, value]) => (
                  <div key={key} className="flex gap-2 min-w-0">
                    <dt className="text-base-content/60 shrink-0">{key}</dt>
                    <dd className="[overflow-wrap:anywhere]">{value || '-'}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </details>
      )}
    </>
  );
}
